// src/components/TypeProduitList.js
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const TypeProduitList = () => {
  const navigate = useNavigate();
  const [allTypes, setAllTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  
  // États pour la recherche
  const [search, setSearch] = useState("");
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Charger la liste des types de produits
  const fetchAllTypes = async () => {
    try {
      setLoading(true);
      setServerError("");
      setInfoMsg("");
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        setLoading(false);
        return;
      }

      const url = `${API_BASE_URL}/stock/type_produit/list_type_produit.php`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json'
        },
        timeout: 10000
      });

      if (response.data?.success) {
        const types = response.data.data || [];
        setAllTypes(types);
        setFilteredTypes(types);
        
        // Supprimé l'alerte "Aucun type de produit trouvé"
        // if (types.length === 0) {
        //   setInfoMsg("Aucun type de produit trouvé.");
        // }
      } else {
        setServerError(response.data?.message || "Erreur lors du chargement des données");
        setAllTypes([]);
        setFilteredTypes([]);
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  };

  // Charger les statistiques des produits
  const fetchStatistiquesProduits = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/stock/produits/statistique.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json'
        },
        timeout: 10000
      });

      if (response.data?.success) {
        // Fusionner les statistiques avec les types existants
        const statsMap = {};
        response.data.data.statistiques.forEach(stat => {
          statsMap[stat.id_type_produit] = stat.nombre_produits;
        });

        // Mettre à jour les types avec les statistiques
        setAllTypes(prevTypes => 
          prevTypes.map(type => ({
            ...type,
            nombre_produits: statsMap[type.id] || 0
          }))
        );
        setFilteredTypes(prevTypes => 
          prevTypes.map(type => ({
            ...type,
            nombre_produits: statsMap[type.id] || 0
          }))
        );
      }
    } catch (err) {
      console.error("Erreur chargement statistiques:", err);
      // Ne pas bloquer l'affichage si les statistiques échouent
    }
  };

  const handleFetchError = (err) => {
    console.error("Erreur chargement types de produits :", err);
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      setServerError("Permission refusée. Vous n'avez pas les droits nécessaires.");
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion.");
    } else if (err.response?.data?.message) {
      setServerError(err.response.data.message);
    } else {
      setServerError("Erreur technique lors du chargement des types de produits.");
    }
    setAllTypes([]);
    setFilteredTypes([]);
  };

  // Charger tous les types et statistiques
  useEffect(() => {
    const loadData = async () => {
      await fetchAllTypes();
      await fetchStatistiquesProduits();
    };
    loadData();
  }, []);

  // Filtrer les types côté client avec la recherche
  useEffect(() => {
    if (allTypes.length > 0) {
      let filtered = [...allTypes];

      if (search.trim()) {
        const searchTerm = search.toLowerCase().trim();
        filtered = filtered.filter(type => 
          type.type && type.type.toLowerCase().includes(searchTerm)
        );
      }

      setFilteredTypes(filtered);
      setCurrentPage(1);
      
      // Supprimé le message d'alerte pour la recherche vide
      // if (filtered.length === 0 && search.trim()) {
      //   setInfoMsg(`Aucun type de produit trouvé pour "${search}"`);
      // } else if (filtered.length === 0) {
      //   setInfoMsg("Aucun type de produit ne correspond aux critères de recherche.");
      // } else {
      //   setInfoMsg("");
      // }
    }
  }, [allTypes, search]);

  // Fonction pour formater le badge de produits
  const getProduitBadge = (count) => {
    if (count === 0 || !count) {
      return (
        <span className="badge fw-semibold" style={{ 
          backgroundColor: '#F5F7FA',
          color: COLORS.darkText,
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }}>
          {count}
        </span>
      );
    } else if (count < 5) {
      return (
        <span className="badge fw-semibold" style={{ 
          backgroundColor: COLORS.infoLight,
          color: COLORS.info,
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }}>
          {count}
        </span>
      );
    } else if (count < 10) {
      return (
        <span className="badge fw-semibold" style={{ 
          backgroundColor: '#fff3e0',
          color: '#f57c00',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }}>
          {count}
        </span>
      );
    } else if (count < 20) {
      return (
        <span className="badge fw-semibold" style={{ 
          backgroundColor: '#fff3e0',
          color: '#ef6c00',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }}>
          {count}
        </span>
      );
    } else {
      return (
        <span className="badge fw-semibold" style={{ 
          backgroundColor: '#ffebee',
          color: COLORS.danger,
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }}>
          {count}
        </span>
      );
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTypes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTypes.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearch("");
  };

  const hasActiveFilters = () => {
    return search.trim() !== "";
  };

  return (
    <Layout>
      {/* Titre de page */}
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion de stocks</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Types de produits</li>
          </ol>
        </nav>
      </div>

      {/* Contenu */}
      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E8F5E9',
                      color: COLORS.success
                    }}>
                      <i className="bi bi-tags"></i>
                    </div>
                    Liste des types de produits
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Gérez les différents types de produits enregistrés dans le système
                </p>
              </div>
              
              <div className="d-flex gap-2">
                <button
                  className="btn fw-semibold"
                  onClick={() => navigate("/product-types/new")}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-plus-lg me-2"></i> 
                  Nouveau type
                </button>
              </div>
            </div>

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {/* Supprimé l'affichage de l'alerte infoMsg */}

            {/* Filtres dans une carte comme ProductList */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <div className="row g-3 align-items-center">
                      {/* Recherche par type */}
                      <div className="col-md-10">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary,
                            height: '100%'
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher par type..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText,
                              height: '38px'
                            }}
                          />
                        </div>
                      </div>

                      {/* Boutons de filtres */}
                      <div className="col-md-2">
                        <div className="d-flex gap-2" style={{ height: '38px' }}>
                          <button 
                            type="button" 
                            className="btn fw-semibold flex-fill d-flex align-items-center justify-content-center"
                            onClick={() => {}}
                            style={{
                              color: COLORS.white,
                              backgroundColor: COLORS.secondary,
                              borderColor: COLORS.secondary,
                              borderRadius: '6px',
                              height: '100%',
                              minHeight: '38px',
                              padding: '0 12px'
                            }}
                          >
                            <i className="bi bi-search"></i>
                          </button>
                          <button 
                            type="button" 
                            className="btn fw-semibold flex-fill d-flex align-items-center justify-content-center"
                            onClick={resetFilters}
                            style={{
                              color: COLORS.primary,
                              borderColor: COLORS.primary,
                              backgroundColor: 'transparent',
                              borderRadius: '6px',
                              height: '100%',
                              minHeight: '38px',
                              padding: '0 12px',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#E3F2FD';
                              e.target.style.borderColor = COLORS.secondary;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.borderColor = COLORS.primary;
                            }}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Résumé des filtres */}
                    <div className="mt-3 d-flex justify-content-between align-items-center">
                      <div className="text-muted small" style={{ color: COLORS.darkText }}>
                        <i className="bi bi-info-circle me-1"></i>
                        <span className="badge fw-semibold me-2" style={{ 
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}>
                          {filteredTypes.length} type(s) de produit
                          {filteredTypes.length !== allTypes.length && 
                            ` sur ${allTypes.length} au total`}
                        </span>
                        
                        {hasActiveFilters() && (
                          <span>
                            <i className="bi bi-funnel me-1"></i>
                            Recherche: "{search}"
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des types de produits...</p>
              </div>
            ) : allTypes.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-tags fs-1"></i>
                  </div>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun type de produit trouvé</h6>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  Aucun type de produit n'a été enregistré pour le moment.
                </p>
                <button
                  onClick={() => navigate("/product-types/new")}
                  className="btn fw-semibold mt-3"
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-plus-lg me-2"></i> 
                  Ajouter un type
                </button>
              </div>
            ) : (
              <>
                {/* Affichage des résultats filtrés */}
                {filteredTypes.length === 0 && hasActiveFilters() ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-search fs-1"></i>
                      </div>
                    </div>
                    <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun résultat</h6>
                    <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                      Aucun type de produit ne correspond à la recherche "{search}".
                    </p>
                    <div className="d-flex justify-content-center">
                      <button
                        className="btn fw-semibold mt-3"
                        onClick={resetFilters}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '10px 24px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.primary;
                          e.target.style.color = COLORS.white;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = COLORS.primary;
                        }}
                      >
                        <i className="bi bi-x-lg me-2"></i> 
                        Effacer la recherche
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderBottom: `2px solid ${COLORS.accent}`
                          }}>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Type de produit</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', textAlign: 'center' }}>Produits rattachés</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((type, index) => {
                            const produitCount = type.nombre_produits || 0;
                            
                            return (
                              <tr key={type.id} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                                {/* Nom du type avec icône */}
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      <div 
                                        className="rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                        style={{ 
                                          width: '40px',
                                          height: '40px',
                                          fontSize: '14px',
                                          backgroundColor: '#E8F5E9',
                                          color: COLORS.success,
                                          boxShadow: '0 2px 4px rgba(76, 175, 80, 0.1)'
                                        }}
                                        title="Type de produit"
                                      >
                                        <i className="bi bi-tag"></i>
                                      </div>
                                    </div>
                                    <div>
                                      <strong className="d-block mb-1" style={{ color: COLORS.darkText }}>
                                        {type.type || "Type sans nom"}
                                      </strong>
                                    </div>
                                  </div>
                                </td>
                                
                                {/* Nombre de produits avec badge */}
                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                  <div className="d-flex flex-column align-items-center">
                                    {getProduitBadge(produitCount)}
                                    <small className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                                      {produitCount === 1 ? 'produit' : 'produits'}
                                    </small>
                                  </div>
                                </td>
                                
                                {/* Actions */}
                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                  <div className="d-flex gap-2 justify-content-center">
                                    <button
                                      className="btn btn-outline-primary"
                                      onClick={() => navigate(`/product-types/${type.id}/update`)}
                                      title="Modifier"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center">
                          <span className="me-2" style={{ color: COLORS.darkText }}>Afficher :</span>
                          <select 
                            className="form-select form-select-sm w-auto"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText,
                              borderRadius: '6px'
                            }}
                          >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <span className="ms-3" style={{ color: COLORS.darkText }}>
                            <span className="badge fw-semibold me-2" style={{ 
                              backgroundColor: '#E3F2FD',
                              color: COLORS.primary,
                              padding: '4px 12px',
                              borderRadius: '6px'
                            }}>
                              {filteredTypes.length} type{filteredTypes.length > 1 ? 's' : ''} de produit
                              {filteredTypes.length !== allTypes.length ? ' (filtrés)' : ''}
                            </span>
                          </span>
                        </div>
                        
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{
                                  color: currentPage === 1 ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                              >
                                <i className="bi bi-chevron-left"></i>
                              </button>
                            </li>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => {
                                if (totalPages <= 7) return true;
                                return page === 1 || 
                                       page === totalPages || 
                                       (page >= currentPage - 1 && page <= currentPage + 1);
                              })
                              .map((page, index, array) => {
                                if (index > 0 && page > array[index - 1] + 1) {
                                  return (
                                    <React.Fragment key={`ellipsis-${page}`}>
                                      <li className="page-item disabled">
                                        <span className="page-link" style={{ borderColor: '#e0e0e0' }}>...</span>
                                      </li>
                                      <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                        <button 
                                          className="page-link fw-semibold"
                                          onClick={() => handlePageChange(page)}
                                          style={{
                                            backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                            borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                            color: currentPage === page ? COLORS.white : COLORS.primary,
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            margin: '0 2px'
                                          }}
                                        >
                                          {page}
                                        </button>
                                      </li>
                                    </React.Fragment>
                                  );
                                }
                                
                                return (
                                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button 
                                      className="page-link fw-semibold"
                                      onClick={() => handlePageChange(page)}
                                      style={{
                                        backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                        borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                        color: currentPage === page ? COLORS.white : COLORS.primary,
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        margin: '0 2px'
                                      }}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              })}
                            
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{
                                  color: currentPage === totalPages ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                              >
                                <i className="bi bi-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`        
        .btn:focus,
        .form-control:focus,
        .page-link:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .badge {
          font-weight: 500;
        }
        
        /* Styles spécifiques pour aligner les hauteurs */
        .form-control, .form-select {
          height: 38px !important;
          line-height: 1.5;
        }
        
        .input-group-text {
          height: 38px !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .btn {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .row.g-3 {
            margin-bottom: 8px;
          }
          
          .row.g-3 > div {
            margin-bottom: 8px;
          }
        }
        
        /* Amélioration de la lisibilité des tables */
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        /* Styles pour les badges de produits */
        .badge {
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
      `}</style>
    </Layout>
  );
};

export default TypeProduitList;